import { useRef, useEffect, useState, memo, useCallback } from 'react'
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import useNetwork from './common/hooks/networkHook.js';

import Clock from './common/components/Clock.js';
import ActiveJobPanel from './components/ActiveJobPanel.js';
import Epcr from './components/epcr/Epcr.js';
import { SimpleJobPanel } from './components/JobPanel.js';
import { AngularCAD } from './components/CAD.js';
import { AngularSettings } from './components/Settings.js';
import SitRep from './components/SitRep.js';
import Users from './components/settings/Users.js';
import Events from './components/settings/Events.js';
import Glossary from './components/settings/Glossary.js';
import JobTags from './components/settings/JobTags.js';
import ReferenceDocs from './components/settings/ReferenceDocs.js';
import Site from './components/settings/Site.js';
import OutsideAgencies from './components/settings/OutsideAgencies.js';
import StandardReplies from './components/settings/StandardReplies.js';
import { ResizeAwareDiv } from './common/components/ResizeablePanel.js';
import Locations from './components/settings/Locations.js';
import Level from './components/settings/Level.js';
import TaskLists from './components/settings/TaskLists.js';
import Problems from './components/settings/Problems.js';
import Types from './components/settings/Types.js';
import CrewResources from './components/settings/CrewResources.js';
import DocTypes from './components/settings/DocTypes.js';
import SOPs from './components/settings/SOPs.js';
import RequiredComments from './components/settings/RequiredComments.js';
import Contacts from './components/settings/Contacts.js';
import AccountRequests from './components/settings/AccountRequests.js';
import NotificationRules from './components/settings/NotificationRules.js';
import ContactLists from './components/settings/ContactLists.js';
import GeoFencing from './components/settings/GeoFencing.js';
import WeatherInfo from './components/WeatherInfo.js';
import { AlertNav } from './components/AlertInfo.js';
import { AngularPublicForm } from './components/PublicForm.js';
import { AngularResetPassword } from './components/ResetPassword.js';
import { AngularRequestAccount } from './components/RequestAccount.js';
import Signin from './common/components/Signin.js';
import { AngularForgottenPassword } from './components/ForgottenPassword.js';
import { AngularPublicFormGroup } from './components/PublicFormGroup.js';
import useAppState from './common/hooks/appStateHook.js';
import { signIn, signOut } from './common/reducers/initContext.js';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useApp } from './common/components/AppProvider.js';

const componentRegistrations = [
    [Clock, 'clock', ['showDate', 'showZone', 'baseDate', 'skew']],
    [ActiveJobPanel, 'activeJobList', []],
    [SimpleJobPanel, 'jobDetails', ['jobid']],
    [Epcr, 'epcr', ['pcrid', 'type', 'site', 'level']],
    [AngularCAD, 'cad', []],
    [AngularCAD, 'tracker', []],
    [SitRep, 'sitrepEditor', []],
    [Users, 'userEditor', []],
    [Events, 'eventsEditor', []],
    [Glossary, 'glossaryEditor', []],
    [JobTags, 'jobTagEditor', []],
    [OutsideAgencies, 'outsideAgencyEditor', []],
    [ReferenceDocs, 'refDocEditor', []],
    [Site, 'siteEditor', []],
    [Level, 'levelEditor', []],
    [StandardReplies, 'repliesEditor', []],
    [Locations, 'locationEditor', []],
    [TaskLists, 'taskListEditor', []],
    [Problems, 'problemEditor', []],
    [Types, 'typeEditor', []],
    [CrewResources, 'resourceEditor', []],
    [DocTypes, 'doctypeEditor', []],
    [SOPs, 'jobProcedureEditor', []],
    [RequiredComments, 'requiredCommentsEditor', []],
    [Contacts, 'contactEditor', []],
    [AccountRequests, 'accountRequestEditor', []],
    [NotificationRules, 'notificationListEditor', []],
    [ContactLists, 'contactListEditor', []],
    [GeoFencing, 'geofencingEditor', []],
    [WeatherInfo, 'weatherInfo', []],
    [AlertNav, 'alerts', []],
    [AngularSettings, 'settings', []],
    [AngularPublicForm, 'publicformEditor', ['code'], { hideNav: true }],
    [AngularForgottenPassword, 'forgottenPassword', []],
    [AngularResetPassword, 'resetPassword', []],
    [AngularRequestAccount, 'requestAccount', []],
    [AngularPublicFormGroup, 'publicformGroup', ['code'], { hideNav: true }],
    [Signin, 'signin', ['authService'], { imgUrl: '/images/chronicler.png' }]
]

const AngularRootApp = memo(({ template }) => {
    const App = useApp();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const network = useNetwork();
    const divRef = useRef(null);
    const didRunRef = useRef(false);
    const { token } = useAppState();
    const [portals, setPortals] = useState([]);
    const initRef = useRef(null);

    const handleLogin = useCallback(token => {
        signIn(App, dispatch, navigate, token);
    }, [App, dispatch, navigate]);
    const handleLogout = useCallback(() => {
        signOut(App, dispatch, navigate);
    }, [App, dispatch, navigate]);

    useEffect(() => {
        if (!didRunRef.current && process.env.NODE_ENV === 'development') {
            didRunRef.current = true;
        } else {
            return () => {
                console.log('UMOUNT!!')
                if (initRef.current) {
                    const element = window.angular.element(initRef.current);
                    const injector = element.injector();
                    const rootScope = injector.get('$rootScope');
                    const timeout = injector.get('$timeout');
                    const url = window.location.href;
                    rootScope.$apply(() => {
                        const authService = injector.get('authService');
                        authService.removeUpdateNotify(handleLogin);
                        authService.removeUpdateNotify(handleLogout);
                        authService.logout();
                        timeout(() => {
                            rootScope.$destroy();
                            element.empty();
                            window.history.replaceState(null, '', url);
                        })
                    });
                }
            }
        }
    }, [handleLogin, handleLogout]);

    useEffect(() => {
        if (!divRef.current || window.angular.element(divRef.current).injector()) {
            return;
        }
        console.log('mount!')

        // inject components
        const injectComponent = (Clazz, name, params, args = {}) => {
            params = params.map(p => typeof p === 'string' ? { name: p, binding: '=' } : p);
            window.angular.module('CAD').directive(name, ['$rootScope', 'authService', function ($rootScope, authService) {
                let scope = {};
                params.forEach(p => scope[p.name] = p.binding ?? '=');
                return {
                    scope,
                    link: function (scope, element) {
                        if (params.length > 0) {
                            params.forEach(p => {
                                scope.$watch(p.name, function (newVal) {
                                    let props = {};
                                    params.forEach(p => {
                                        if (scope[p.name]) {
                                            props[p.reactName ?? p.name] = scope[p.name]
                                        }
                                        if (p.name === 'authService') {
                                            props.authService = authService;
                                        }
                                    });
                                    setPortals(portals => {
                                        const portal = createPortal(<ResizeAwareDiv><Clazz {...props} {...args}></Clazz></ResizeAwareDiv>, element[0]);
                                        return [...portals.filter(p => p.scope !== scope), { portal, scope }];
                                    });
                                })
                            })
                        } else {
                            setPortals(portals => {
                                const portal = createPortal(<ResizeAwareDiv><Clazz {...args}></Clazz></ResizeAwareDiv>, element[0]);
                                if (portals.every(p => p.portal !== portal)) {
                                    return [...portals, { portal, scope }];
                                } else {
                                    return portals;
                                }
                            });
                        }

                        // Unmount React component when AngularJS directive is destroyed
                        scope.$on('$destroy', function () {
                            setPortals(portals => portals.filter(p => p.scope !== scope));
                        });
                    },
                };
            }]);
        }
        if (!window.angularBootstrapped) {
            componentRegistrations.forEach(c => injectComponent(...c));
        }
        initRef.current = divRef.current;
        const $injector = window.angular.bootstrap(divRef.current, ['CAD']);
        const authService = $injector.get('authService');
        const $compile = $injector.get('$compile');
        const $rootScope = $injector.get('$rootScope');

        // While authentication is managed by angular - reflect the token into React
        if (!authService.token && token) {
            authService.login(token);
        }

        authService.onLogin(handleLogin);
        authService.onLogout(handleLogout);
        window.angularBootstrapped = true;


        let html = $compile(template ?? `
        <div class="container-fluid">
            <div ng-view></div>
        </div>
        <div class="device-xs visible-lg visible-md visible-sm visible-xs   "></div>
        <div class="device-sm visible-lg visible-md visible-sm"></div>
        <div class="device-md visible-lg visible-md"></div>
        <div class="device-lg visible-lg"></div>`
        )($rootScope);
        const element = window.angular.element(divRef.current);
        element.empty();
        element.append(html);
    }, [divRef, dispatch, network, portals, token, navigate, template, handleLogin, handleLogout]);

    return (
        <div key="root" ref={divRef} style={{ width: '100%', height: '100%' }}>{portals.map(p => p.portal)}</div>
    );
})
AngularRootApp.propTypes = {
    template: PropTypes.string
}

export default AngularRootApp;
